import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { useMemo, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { fetchProducts, type ShopifyProduct } from "@/lib/shopify";
import { ProductCard } from "@/components/site/ProductCard";
import { EmptyProducts } from "@/components/site/EmptyProducts";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const AUDIENCES = [
  { key: "alopecia", label: "Alopecia-friendly" },
  { key: "chemo", label: "Chemo-friendly" },
  { key: "religious", label: "Religious / Modest" },
  { key: "fashion", label: "Fashion" },
] as const;

const TEXTURES = ["Straight", "Wavy", "Loose Curl"];
const CAPS = ["Monofilament Top", "Silk Top", "French Top"];
const COLORS = ["Blonde", "Light Brown", "Chestnut", "Brunette", "Black", "Auburn", "Grey"];


const searchSchema = z.object({
  audience: z.enum(["alopecia", "chemo", "religious", "fashion"]).optional(),
  texture: z.string().optional(),
  cap: z.string().optional(),
  color: z.string().optional(),
});

export const Route = createFileRoute("/shop")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Shop wigs — European Wig Luxury" },
      { name: "description", content: "Browse our collection of short-length virgin European hair wigs. Filter by color, texture, cap construction, and needs." },
      { property: "og:title", content: "Shop virgin European hair wigs" },
      { property: "og:description", content: "Hand-tied short wigs, $500–$1,000." },
      { property: "og:url", content: "https://europeanwigluxury.com/shop" },
      { property: "og:image", content: "https://europeanwigluxury.com/og-image.jpg" },
      { name: "twitter:image", content: "https://europeanwigluxury.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://europeanwigluxury.com/shop" }],
  }),
  component: Shop,
});

function hasTag(p: ShopifyProduct, prefix: string, value: string) {
  const v = value.toLowerCase();
  return p.node.tags.some((t) => {
    const lower = t.toLowerCase();
    return lower === `${prefix}:${v}` || lower === v || lower.includes(v);
  });
}

function Shop() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/shop" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { data: products, isLoading } = useQuery({ queryKey: ["products", "all"], queryFn: () => fetchProducts("product_type:Wig") });

  const filtered = useMemo(() => {
    let list = products ?? [];
    if (search.audience) list = list.filter((p) => hasTag(p, "audience", search.audience!));
    if (search.texture) list = list.filter((p) => hasTag(p, "texture", search.texture!));
    if (search.cap) list = list.filter((p) => hasTag(p, "cap", search.cap!));
    if (search.color) list = list.filter((p) => hasTag(p, "color", search.color!));
    return list;
  }, [products, search]);

  const setFilter = (key: keyof typeof search, value: string | undefined) => {
    navigate({ search: (prev: typeof search) => ({ ...prev, [key]: value }) });
  };
  const clear = () => navigate({ search: {} });

  const activeCount = [search.audience, search.texture, search.cap, search.color].filter(Boolean).length;
  const hasAny = activeCount > 0;

  const filterPanel = (
    <div className="space-y-8">
      <FilterGroup label="Shop by need">
        <FilterList
          current={search.audience}
          options={AUDIENCES.map((a) => ({ key: a.key, label: a.label }))}
          onSelect={(v) => setFilter("audience", v)}
        />
      </FilterGroup>
      <FilterGroup label="Texture">
        <FilterList
          current={search.texture}
          options={TEXTURES.map((t) => ({ key: t.toLowerCase(), label: t }))}
          onSelect={(v) => setFilter("texture", v)}
        />
      </FilterGroup>
      <FilterGroup label="Cap construction">
        <FilterList
          current={search.cap}
          options={CAPS.map((t) => ({ key: t.toLowerCase(), label: t }))}
          onSelect={(v) => setFilter("cap", v)}
        />
      </FilterGroup>
      <FilterGroup label="Color">
        <FilterList
          current={search.color}
          options={COLORS.map((t) => ({ key: t.toLowerCase(), label: t }))}
          onSelect={(v) => setFilter("color", v)}
        />
      </FilterGroup>
      {hasAny && (
        <Button variant="outline" size="sm" onClick={clear}>Clear all filters</Button>
      )}
    </div>
  );

  return (
    <>
      <section className="border-b border-border/70 bg-secondary/40">
        <div className="container-luxe py-12 md:py-20">
          <p className="eyebrow">The collection</p>
          <h1 className="mt-3 font-display text-[2.25rem] leading-[1.05] md:text-6xl">Short-length wigs</h1>
          <p className="mt-4 max-w-xl text-sm text-muted-foreground md:text-base">
            Every piece is 100% virgin European hair — unprocessed, cuticle-aligned, and hand-tied.
            Priced $500–$1,000.
          </p>
        </div>
      </section>

      <section className="container-luxe py-8 md:py-14">
        <div className="grid gap-10 lg:grid-cols-[220px_1fr]">
          <aside className="hidden lg:block">{filterPanel}</aside>

          <div>
            <div className="mb-6 flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {isLoading ? "Loading…" : `${filtered.length} piece${filtered.length === 1 ? "" : "s"}`}
              </p>
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="lg:hidden">
                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                    Filters{activeCount > 0 ? ` · ${activeCount}` : ""}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[85vw] max-w-sm overflow-y-auto">
                  <SheetHeader className="text-left">
                    <SheetTitle className="font-display text-2xl">Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">{filterPanel}</div>
                </SheetContent>
              </Sheet>
            </div>
            {filtered.length ? (
              <div className="grid gap-6 sm:grid-cols-2 sm:gap-8 xl:grid-cols-3">
                {filtered.map((p) => <ProductCard key={p.node.id} product={p} />)}
              </div>
            ) : (
              <EmptyProducts />
            )}
          </div>
        </div>
      </section>
    </>
  );
}


function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="eyebrow mb-3">{label}</p>
      {children}
    </div>
  );
}

function FilterList({
  current, options, onSelect,
}: {
  current?: string;
  options: Array<{ key: string; label: string }>;
  onSelect: (v: string | undefined) => void;
}) {
  return (
    <ul className="space-y-1.5">
      {options.map((o) => {
        const active = current === o.key;
        return (
          <li key={o.key}>
            <button
              onClick={() => onSelect(active ? undefined : o.key)}
              className={`w-full text-left text-sm transition-colors ${active ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}
            >
              {active ? "✓ " : ""}{o.label}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
